"use client";

import Image from "next/image";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { VideoPlayerProps } from "@/types/ui";
import { getWindowDimensions } from "@/lib/helper";

const VideoPlayer = ({ imageAlt, videoSrc, videoIcon }: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldLoadVideo] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  // Calculate initial poster image based on viewport size (SSR-safe)
  // This prevents layout shift by determining the correct poster before first paint
  const getInitialPoster = (): string => {
    if (typeof window === "undefined") return "/assets/370x220-small.webp";
    const width = window.innerWidth;
    if (width < 768) return "/assets/370x220-small.webp";
    if (width < 1024) return "/assets/824x464.webp";
    if (width < 1280) return "/assets/1480x880.webp";
    return "/assets/1920x1080.webp";
  };

  const [currentPosterImage, setCurrentPosterImage] =
    useState<string>(getInitialPoster);

  // Use useLayoutEffect to update poster synchronously before browser paint
  // This prevents visible layout shift when poster changes
  useLayoutEffect(() => {
    const updatePosterImage = () => {
      const { width } = getWindowDimensions(true);
      let newPoster: string;

      if (width < 768) {
        newPoster = "/assets/370x220-small.webp";
      } else if (width < 1024) {
        newPoster = "/assets/824x464.webp";
      } else if (width < 1280) {
        newPoster = "/assets/1480x880.webp";
      } else {
        newPoster = "/assets/1920x1080.webp";
      }

      // Only update if poster actually changed to avoid unnecessary re-renders
      if (newPoster !== currentPosterImage) {
        setCurrentPosterImage(newPoster);
      }
    };

    // Update immediately on mount (before paint)
    updatePosterImage();

    const handleResize = () => {
      updatePosterImage();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentPosterImage]);
  const isYouTubeUrl = (url: string): boolean => {
    return Boolean(
      url && (url.includes("youtube.com") || url.includes("youtu.be"))
    );
  };
  useEffect(() => {
    if (shouldLoadVideo && videoRef.current) {
      const playVideo = async () => {
        try {
          await videoRef.current?.play();
          setIsPlaying(true);
        } catch (error) {
          console.log("Autoplay prevented:", error);
        }
      };
      playVideo();
    }
  }, [shouldLoadVideo]);

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  if (videoSrc && isYouTubeUrl(videoSrc)) {
    return (
      <div className="absolute inset-0 w-full h-full">
        <iframe
          src={videoSrc}
          title={imageAlt || "Video"}
          className="absolute inset-0 w-full h-full shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25),0px_0px_15px_0px_rgba(0,0,0,0.07)]"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          aria-label={imageAlt || "Embedded video content"}
          role="application"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 aspect-video size-full">
      <div
        className="relative w-full h-full"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {shouldLoadVideo && videoSrc && (
          <video
            ref={videoRef}
            width="100%"
            height="100%"
            controls={isPlaying}
            poster={currentPosterImage}
            preload="auto"
            muted
            playsInline
            aria-label={
              imageAlt ||
              "Video showing pharmacy marketing and growth solutions."
            }
            role="application"
            // @ts-expect-error - fetchPriority is not in TypeScript types but is valid HTML attribute
            fetchPriority="high"
            className="w-full !h-full object-contain md:object-fill shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25),0px_0px_15px_0px_rgba(0,0,0,0.07)]"
            style={{ willChange: "transform" }}
            suppressHydrationWarning
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}

        {!isPlaying && isHovering && (
          <button
            onClick={handlePlay}
            aria-label={isPlaying ? "Pause video" : "Play video"}
            aria-pressed={isPlaying}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer z-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-opacity-50"
          >
            <div className="relative xl:w-[100px] lg:w-[90px] md:w-20 w-[70px] aspect-square rounded-full  transition-all duration-300 group-hover:scale-105 group-active:scale-95 ">
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-[#F6842B] to-[#F6842B]/95 flex items-center justify-center group-hover:from-[#F6842B] group-hover:to-[#F6842B] transition-all duration-300 shadow-[0_4px_15px_rgba(0,0,0,0.1),inset_0_1px_2px_rgba(246,132,43,0.8)]">
                {videoIcon ? (
                  <Image
                    src={videoIcon}
                    alt="play icon"
                    width={40}
                    height={40}
                    priority
                    quality={90}
                    className="xl:w-[40px] lg:w-[35px] md:w-[32px] w-[25px] group-hover:scale-105 transition-transform duration-200 filter brightness-0 invert"
                  />
                ) : (
                  <div className="xl:w-[40px] lg:w-[35px] md:w-[32px] w-[25px] xl:h-[40px] lg:h-[35px] md:h-[32px] h-[25px] flex items-center justify-center group-hover:scale-105 transition-transform duration-200 ml-0.5">
                    <svg
                      viewBox="0 0 24 24"
                      fill="url(#playGradientWhite)"
                      className="w-full h-full"
                    >
                      <defs>
                        <linearGradient
                          id="playGradientWhite"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#ffffff" />
                          <stop offset="50%" stopColor="#ffffff" />
                          <stop offset="100%" stopColor="#ffffff" />
                        </linearGradient>
                      </defs>
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}
              </div>

              <div
                className="absolute inset-0 rounded-full border border-white/40 animate-ping opacity-70"
                style={{
                  animationDelay: "0.3s",
                  animation: "pulseCTA2 2s ease-in-out infinite",
                }}
              ></div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
